/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import _ from "underscore";
import analytics from "analytics/analytics";
import requester from "./requester";
import eventEmitter from "mixins/event-emitter";
import url from "../utils/url";

let cache = {};

let csrfToken = null;

/**
 * Generic success handler for requests to the Rothko API.
 * @param {number} requestId
 * @param {string} cacheKey
 * @param {object} responseJSON
 */
function successHandler(requestId, cacheKey, responseJSON) {
    var data = responseJSON.data;
    csrfToken = responseJSON.csrf_token;

    if (this._options.cache) {
        cache[cacheKey] = responseJSON;
    }

    if (requestId === this._requestId) {
        this.trigger("data", data);
    }

    analytics.updatePageViewInAnalyticsCookie();

    return data;
}

/**
 * Creates a cache key from a template name and a query object.
 * @param {string} name
 * @param {object|URLSearchParams} query
 * @returns {string}
 */
function getCacheKey(name, query) {
    if (query instanceof url.URLSearchParams) {
        query = query.toJSON();
    }

    var queryKeys = Object.keys(query || {});
    queryKeys.sort();

    return queryKeys.reduce(function(result, key) {
        return result + key + "=" + query[key];
    }, name + ":");
}

/**
 * @param {string} name The name of the template to fetch data for.
 * @param {object} [options]
 * @param {boolean=true} [options.cache] Whether or not to cache the response.
 * @constructor
 */
function Rothko(name, options) {
    this._templateName = name;
    this._options = _.extend(
        {},
        {
            cache: true
        },
        options
    );
    this._requestId = 0;
}

_.extend(Rothko.prototype, eventEmitter, {
    /**
     * Fetches the data for the template with the given params. Returns a Promise that will
     * resolve with the data returned from the API.
     *
     * If by the time the response has been received no other requests have been started on this
     * instance a 'data' event will be also be fired and if options.cache is true the response
     * will be cached.
     *
     * @param {object|URLSearchParams} [query] The query params for this request.
     * @returns {Promise}
     */
    fetch: function(query) {
        // increment the request id so this request will have a new id
        this._requestId++;

        // generate a cache key for this request
        var cacheKey = getCacheKey(this._templateName, query);

        // create a bound version of the successHandler function with the `requestId` and
        // `cacheKey` arguments pre-filled.
        var boundSuccessHandler = successHandler.bind(this, this._requestId, cacheKey);

        // if this instance is using the cache and we have an entry in the cache for this
        // cacheKey then resolve without making a network request. Using Promise.resolve()
        // as we still want to guarantee async behaviour.
        if (this._options.cache && cache.hasOwnProperty(cacheKey)) {
            return Promise.resolve(cache[cacheKey]).then(boundSuccessHandler);
        }

        // otherwise make the API call
        var request = requester("/api/rothko/" + this._templateName + "/", {
            query: query
        });

        return request.then(requester.toJSON).then(boundSuccessHandler);
    }
});

export default {
    Rothko: Rothko,

    /**
     *
     * @param {string} name The name of the template to fetch data for.
     * @param {object|URLSearchParams} [query] The query params for this request.
     * @param {object} [options] Options to pass to the Rothko instance.
     * @returns {Promise}
     */
    fetch: function(name, query, options) {
        var rothko = new Rothko(name, options);
        return rothko.fetch(query);
    },

    /**
     * Clears the cache for either a specific template name or all templates.
     * @param {string} [name]
     */
    clearCache: function(name) {
        if (arguments.length === 0) {
            cache = {};
            return;
        }

        cache = Object.keys(cache).reduce(function(result, key) {
            if (key.indexOf(name + ":") !== 0) {
                result[key] = cache[key];
            }

            return result;
        }, {});
    },

    getCSRF() {
        return csrfToken;
    }
};

